/**
 * ============================================================
 *  MATERIAL 3 DESIGN TOKENS
 * ============================================================
 *
 *  Source of truth for M3 colour roles, shape, spacing, elevation
 *  and motion. palette.js and organizationPalette.js derive their
 *  surface, border and text colours from these roles, so MUI
 *  components, sx code and the M3 kit (src/components/m3) render
 *  the same colours.
 *
 *  Colour roles were generated once with
 *  @material/material-color-utilities (HCT tonal palettes):
 *    - primary   : seed #659d33 (Arivu logo green). The light-mode
 *                  primary is pinned to the exact logo colour.
 *    - secondary : same hue as primary, chroma 16 (muted green)
 *    - tertiary  : #b45309 (existing secondary.main amber)
 *    - neutral   : primary hue, chroma 2 (near-grey, matches logo grey #6b6b6b)
 *    - status    : success #16a34a, warning #d97706, info #0284c7, error #ba1a1a
 *  Light roles use tones 40/90/30, dark roles use tones 80/20/30/90.
 *
 *  HOW TO USE
 *  ----------
 *  sx code      : const m3 = getM3Colors(isDark); sx={{ color: m3.onSurface }}
 *  CSS modules  : color: var(--m3-on-surface);
 *  The CSS variables are injected by injectM3Tokens() (called from
 *  ThemeProviderWrapper) and switch with the html[data-theme] attribute.
 * ============================================================
 */

// ── Brand ────────────────────────────────────────────────────────────────────
export const M3_BRAND = {
  primary: "#659d33",   // Arivu logo green
  neutral: "#6b6b6b",   // Arivu logo grey
};

// ── Colour roles ─────────────────────────────────────────────────────────────
export const M3_COLORS = {
  light: {
    primary: M3_BRAND.primary,
    onPrimary: "#ffffff",
    primaryContainer: "#cdecad",
    onPrimaryContainer: "#295000",
    secondary: "#57624a",
    onSecondary: "#ffffff",
    secondaryContainer: "#dae7c9",
    onSecondaryContainer: "#3f4a34",
    tertiary: "#9b4500",
    onTertiary: "#ffffff",
    tertiaryContainer: "#ffdbca",
    onTertiaryContainer: "#763300",
    error: "#ba1a1a",
    onError: "#ffffff",
    errorContainer: "#ffdad5",
    onErrorContainer: "#930009",
    success: "#006e2d",
    onSuccess: "#ffffff",
    successContainer: "#bdefbf",
    onSuccessContainer: "#005320",
    warning: "#904d00",
    onWarning: "#ffffff",
    warningContainer: "#ffdcc3",
    onWarningContainer: "#6e3900",
    info: "#006398",
    onInfo: "#ffffff",
    infoContainer: "#cce5ff",
    onInfoContainer: "#004b73",
    surface: "#fcf9f5",
    surfaceDim: "#dcdad6",
    surfaceBright: "#fcf9f5",
    surfaceContainerLowest: "#ffffff",
    surfaceContainerLow: "#f6f3ef",
    surfaceContainer: "#f0edea",
    surfaceContainerHigh: "#eae8e4",
    surfaceContainerHighest: "#e4e2de",
    onSurface: "#1b1c1a",
    onSurfaceVariant: "#454840",
    outline: "#75786f",
    outlineVariant: "#c5c7bd",
    inverseSurface: "#30302e",
    inverseOnSurface: "#f3f0ed",
    inversePrimary: "#a4d577",
    scrim: "#000000",
    // State layers (on-surface at M3 state opacities)
    stateHover: "rgba(27, 28, 26, 0.08)",
    statePressed: "rgba(27, 28, 26, 0.12)",
    stateSelected: "rgba(101, 157, 51, 0.12)",
    focusRing: "0 0 0 3px rgba(101, 157, 51, 0.35)",
  },
  dark: {
    primary: "#a4d577",
    onPrimary: "#1a3700",
    primaryContainer: "#295000",
    onPrimaryContainer: "#bff290",
    secondary: "#becbae",
    onSecondary: "#29341f",
    secondaryContainer: "#3f4a34",
    onSecondaryContainer: "#dae7c9",
    tertiary: "#ffb68e",
    onTertiary: "#532200",
    tertiaryContainer: "#763300",
    onTertiaryContainer: "#ffdbca",
    error: "#ffb4ab",
    onError: "#690004",
    errorContainer: "#930009",
    onErrorContainer: "#ffdad5",
    success: "#62df7d",
    onSuccess: "#003914",
    successContainer: "#005320",
    onSuccessContainer: "#bdefbf",
    warning: "#ffb77d",
    onWarning: "#4d2600",
    warningContainer: "#6e3900",
    onWarningContainer: "#ffdcc3",
    info: "#93ccff",
    onInfo: "#003351",
    infoContainer: "#004b73",
    onInfoContainer: "#cce5ff",
    surface: "#131412",
    surfaceDim: "#131412",
    surfaceBright: "#393937",
    surfaceContainerLowest: "#0e0e0d",
    surfaceContainerLow: "#1b1c1a",
    surfaceContainer: "#1f201e",
    surfaceContainerHigh: "#2a2a28",
    surfaceContainerHighest: "#353533",
    onSurface: "#e4e2de",
    onSurfaceVariant: "#c5c7bd",
    outline: "#8f9288",
    outlineVariant: "#454840",
    inverseSurface: "#e4e2de",
    inverseOnSurface: "#30302e",
    inversePrimary: M3_BRAND.primary,
    scrim: "#000000",
    stateHover: "rgba(228, 226, 222, 0.08)",
    statePressed: "rgba(228, 226, 222, 0.12)",
    stateSelected: "rgba(164, 213, 119, 0.16)",
    focusRing: "0 0 0 3px rgba(164, 213, 119, 0.40)",
  },
};

/** Returns the colour roles for the active mode. */
export const getM3Colors = (isDark) => (isDark ? M3_COLORS.dark : M3_COLORS.light);

// ── Shape (px) ───────────────────────────────────────────────────────────────
// md = cards/tables/charts (ORG_PALETTE.radius.card), sm = inputs, lg = sheets.
export const M3_SHAPE = { xs: 6, sm: 8, md: 12, lg: 16, xl: 28, full: 9999 };

// ── Spacing (px) ─────────────────────────────────────────────────────────────
export const M3_SPACE = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 24, xl: 32 };

// ── Typography (px) ──────────────────────────────────────────────────────────
// Same scale as ORG_PALETTE.font.size (kept here to avoid an import cycle,
// since organizationPalette.js reads its colours from this file).
export const M3_TYPE = {
  family: "Poppins, sans-serif",
  size: { display: 32, headline: 20, title: 18, titleSm: 16, body: 14, caption: 12 },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
};

// ── Elevation ────────────────────────────────────────────────────────────────
export const M3_ELEVATION = {
  light: {
    1: "0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.08)",
    2: "0 2px 6px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.08)",
    3: "0 12px 40px rgba(0, 0, 0, 0.18)",
  },
  dark: {
    1: "0 1px 2px rgba(0, 0, 0, 0.30), 0 1px 3px rgba(0, 0, 0, 0.40)",
    2: "0 2px 6px rgba(0, 0, 0, 0.40), 0 8px 24px rgba(0, 0, 0, 0.40)",
    3: "0 12px 40px rgba(0, 0, 0, 0.60)",
  },
};

/** Returns the elevation shadows for the active mode. */
export const getM3Elevation = (isDark) => (isDark ? M3_ELEVATION.dark : M3_ELEVATION.light);

// ── Motion ───────────────────────────────────────────────────────────────────
export const M3_MOTION = {
  easing: "cubic-bezier(0.2, 0, 0, 1)",
  durationShort: "150ms",
  durationMedium: "200ms",
};

// ── Data grid ────────────────────────────────────────────────────────────────
export const M3_GRID = { headerHeight: 48, rowHeight: 48 };

// ── CSS variable generation ──────────────────────────────────────────────────
const kebab = (key) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

const varLines = (prefix, obj, unit = "") =>
  Object.entries(obj).map(([k, v]) => `--m3-${prefix}${kebab(String(k))}: ${typeof v === "number" ? `${v}${unit}` : v};`);

const modeVars = (mode) =>
  [...varLines("", M3_COLORS[mode]), ...varLines("elevation-", M3_ELEVATION[mode])].join("\n  ");

/** CSS text for every token: light on :root, dark on [data-theme="dark"]. */
export const buildM3CssVariables = () => {
  const shared = [
    `--m3-font: ${M3_TYPE.family};`,
    ...varLines("font-size-", M3_TYPE.size, "px"),
    ...varLines("font-weight-", M3_TYPE.weight),
    ...varLines("radius-", M3_SHAPE, "px"),
    ...varLines("space-", M3_SPACE, "px"),
    ...varLines("grid-", M3_GRID, "px"),
    `--m3-ease: ${M3_MOTION.easing};`,
    `--m3-duration-short: ${M3_MOTION.durationShort};`,
    `--m3-duration-medium: ${M3_MOTION.durationMedium};`,
    `--m3-brand: ${M3_BRAND.primary};`,
  ].join("\n  ");

  return `:root {\n  ${shared}\n  ${modeVars("light")}\n}\n[data-theme="dark"] {\n  ${modeVars("dark")}\n}\n`;
};

/** Injects the token stylesheet once. Safe to call repeatedly. */
export const injectM3Tokens = () => {
  if (typeof document === "undefined") return;
  const id = "arivu-m3-tokens";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = buildM3CssVariables();
  document.head.appendChild(style);
};
